import { Component, OnInit } from '@angular/core';
import { ApplicationConfigurationService } from '@stg-js-ngx-core/oauth';
import { SyncopeService } from './common/services/syncope.service';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, map } from 'rxjs/operators';
import { Title } from '@angular/platform-browser';
import { LoggedInUserDetailsService } from './common/services/logged-in-user-details.service';
import { LanguageService } from './common/services/language.service';
import { RealmsDetailsService } from './common/services/realms-details.service';
import { forkJoin } from 'rxjs';
import { EMAIL_REGEX } from "./common/constants/constants";
declare var aaimsCommonService: any;

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit {
  title = 'aaims-ui-ngx';
  appLoad: boolean = false;
  loadPages: boolean = false;
  logo: string = '/uix/static/assets/images/aaims-logo.png';
  profileImageUrl: string = '/uix/static/assets/images/genericAvatar.png';
  menuItems: any = [
    { title: 'Home', page: '/home', iconClass: 'fas fa-home' },
    {
      title: 'Franchise Associate Management',
      page: '/franchise-associate-management',
      iconText: 'FAM',
      stgSecuredObject: 'Franchise User Management'
    },
    {
      title: 'Client Associate Management',
      page: '/client-associate-management/user-management',
      iconText: 'CAM',
      stgSecuredObject: 'Client Associates User Management',
    },
    {
      title: 'User Management',
      page: '/user-management',
      iconText: 'UM',
      stgSecuredObject: 'User Administration'
    },
    {
      title: 'Associate Early Account Deactivation',
      page: '/associate-early-account-deactivation',
      iconText: 'AEA',
      stgSecuredObject:'Disabled Associates User Management'
    },
    {
      title: 'Admin Support',
      page: '/admin-support',
      iconClass: 'fas fa-user-headset',
      stgSecuredObject: 'Admin Support',
      submenu: [
        {
          title: 'Validate Identity',
          page: '/admin-support/validate-identity',
          iconClass: 'fal fa-id-badge',
          stgSecuredObject: 'Validate Identity'
        },
        {
          title: 'Global User Search',
          page: '/admin-support/global-user-search',
          iconClass: 'fas fa-search',
          stgSecuredObject: 'Global User Search'
        }
      ]
    }
  ];
  profileMenuItems: any = [];
  selfService: boolean = false;

  constructor(
    public activatedRoute: ActivatedRoute,
    private syncopeService: SyncopeService,
    private appConfigService: ApplicationConfigurationService,
    private router: Router,
    private titleService: Title,
    private loggedInUserDetailsService: LoggedInUserDetailsService,
    private languageService: LanguageService,
    private realmsDetailsService: RealmsDetailsService
    ) {
    aaimsCommonService.setRootContext('/uix');
    this.activatedRoute.queryParams.subscribe(params => {
      if(params && params['locale']){
        this.languageService.lang = params['locale'];
      } else {
        this.languageService.lang = navigator.language;
      }
    });
  }

  ngOnInit() {
    this.loadApp();

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => {
          let route: ActivatedRoute = this.router.routerState.root;
          let routeTitle = '';
          while (route!.firstChild) {
            route = route.firstChild;
          }
          if (route.snapshot.data['pageTitle']) {
            routeTitle = route!.snapshot.data['pageTitle'];
          }
          return routeTitle;
        })
      )
      .subscribe((title: string) => {
        if (title) {
          this.titleService.setTitle(`AAIMS | ${title}`);
        }
      });
  }

  loadApp() {
    this.setUserOffset();

    if (this.isSelfServiceMode()) {
      this.loadSelfServiceApp();
    } else {
      this.loadMainApp();
    }
  }

  private setUserOffset(): void {
    const offset = this.appConfigService.applicationConfig.offset - Date.now();
    this.loggedInUserDetailsService.setUserOffset(offset);
  }

  private isSelfServiceMode(): boolean {
    return window.location.href.indexOf('selfservice') > -1;
  }

  private loadSelfServiceApp(): void {
    this.selfService = true;
    forkJoin([
      this.realmsDetailsService.getRealmsDetails(true),
      this.syncopeService.getConfigurations(true)
    ]).subscribe(
      () => this.appLoad = true,
      (err) => {
        console.error(err);
        this.appLoad = true;
      }
    );
  }

  private loadMainApp(): void {
    this.loadGoogleMapsScript();
    this.appLoad = true;

    forkJoin([
      this.syncopeService.getSyncopeUser(),
      this.realmsDetailsService.getRealmsDetails(),
      this.syncopeService.getConfigurations()
    ]).subscribe(
      (res) => this.handleMainAppSuccess(res),
      (err) => {
        this.loadPages = true;
        console.error(err);
      }
    );
  }

  private loadGoogleMapsScript(): void {
    const key = this.appConfigService.applicationConfig.googleMaps.key;
    this.loadScript(`https://maps.googleapis.com/maps/api/js?libraries=places&key=${key}`);
  }

  private handleMainAppSuccess(res: any[]): void {
    if (this.isValidUserResponse(res[0])) {
      this.setupUserProfile(res[0]);
    }
    this.loadPages = true;
  }

  private isValidUserResponse(userResponse: any): boolean {
    return userResponse?.data?.plainAttrs;
  }

  private setupUserProfile(userResponse: any): void {
    this.syncopeService.syncopeUserObj = userResponse.data;
    this.loggedInUserDetailsService.setData(userResponse);

    if (this.syncopeService.syncopeUserObj?.realm) {
      this.createProfileMenu();
    }
  }

  private createProfileMenu(): void {
    const username = this.syncopeService.syncopeUserObj.username;
    const encodedUsername = EMAIL_REGEX.test(username) ? encodeURIComponent(username) : username;
    this.profileMenuItems = [{
      title: 'My Profile',
      page: `/profile/${encodedUsername}`,
      iconClass: 'fas fa-user'
    }];
  }

  loadScript(name: string) {
    let script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = name;
    document.getElementsByTagName('head')[0].appendChild(script);
  }
}
